import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { STALE_TIME_MS } from "../../../hooks/constants";
import { USER_GROUPS_QUERY_KEY } from "../../../hooks/useAllUserGroupsForACompany";

export const USER_GROUP_QUERY_KEY = "userGroupById";

interface UseUserGroupByIdResult {
  userGroup: UserManagement_V1.UserGroup | undefined;
  isLoading: boolean;
  error: Error | null;
  isStale: boolean;
  refetchUserGroup: () => void;
}

export const useUserGroupById = (groupId?: string): UseUserGroupByIdResult => {
  const userManagementService = useUserManagementServiceV1();
  const queryClient = useQueryClient();

  const {
    data: userGroup,
    isLoading,
    error,
    isStale,
    refetch,
  } = useQuery({
    queryKey: [USER_GROUP_QUERY_KEY, groupId],
    queryFn: async () => {
      if (!groupId) throw new Error("Group ID is required");

      return await userManagementService.getUserGroupById(groupId);
    },
    enabled: !!groupId,
    staleTime: STALE_TIME_MS,
    initialData: () => {
      if (!groupId) return undefined;

      const queries = queryClient.getQueriesData<UserManagement_V1.UserGroup[]>(
        {
          queryKey: [USER_GROUPS_QUERY_KEY],
        },
      );

      for (const [, groups] of queries) {
        if (!groups) continue;
        const found = groups.find((g) => g.userGroupId === groupId);
        if (found) return found;
      }

      return undefined;
    },
  });

  const refetchUserGroup = useCallback(() => {
    void refetch();
  }, [refetch]);

  return useMemo(
    () => ({
      userGroup,
      isLoading,
      error,
      isStale,
      refetchUserGroup,
    }),
    [userGroup, isLoading, error, isStale, refetchUserGroup],
  );
};
